import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Select,
  Steps,
  Row,
  Col,
  Switch,
  Button,
} from "antd";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { AVAILABLE_CURRENCIES } from "../../../utils/useCurrency";

const { Option } = Select;
const PHONE_PATTERN = /^[+]?[0-9\s\-()]{8,15}$/;

// FIX: Added licenseId, usersNumber, customersNumber, and pointsMonthlyAmount
// so they are included in the payload sent to the backend.
const EDITABLE_FIELDS = [
  "companyName",
  "companyTaxNumber",
  "companyEmail",
  "companyPhone",
  "companyAddress",
  "companyFax",
  "companyTown",
  "companyPostalCode",
  "companyCountry",
  "companyCurrency",
  "companyBirthdate",
  "companyCapital",
  "companyEmployeesNumber",
  "companyTypeId",
  "licenseId", // Added
  "licenseStartDate",
  "licenseEndDate",
  "licenseIsMonthlyPayed",
  "licenseIsYearlyPayed",
  "companyIsInTrialPeriod",
  "trialPeriodStartDate",
  "trialPeriodEndDate",
  "companyIsActive",
  "usersNumber", // Added
  "customersNumber", // Added
  "pointsMonthlyAmount", // Added
];

const fmtDate = (d) => (d?.format ? d.format("YYYY-MM-DD") : (d ?? null));

const DATE_FIELDS = [
  "companyBirthdate",
  "licenseStartDate",
  "licenseEndDate",
  "trialPeriodStartDate",
  "trialPeriodEndDate",
];

const toDayjs = (values) => {
  const out = { ...values };
  DATE_FIELDS.forEach((f) => {
    out[f] = values[f] ? dayjs(values[f]) : null;
  });
  return out;
};

const CompanyFormModal = ({
  open,
  onCancel,
  onSubmit,
  isEdit,
  initialData,
  isReadMode,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const isInTrial = Form.useWatch("companyIsInTrialPeriod", form);
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({});
  const { companyTypes } = useSelector((state) => state.companyType);

  useEffect(() => {
    if ((isEdit || isReadMode) && initialData) {
      const initialFormData = toDayjs(initialData);
      setCurrentStep(0);
      setFormData(initialFormData);
      form.setFieldsValue(initialFormData);
    } else {
      setCurrentStep(0);
      setFormData({});
      form.resetFields();
      form.setFieldsValue({ companyIsActive: true });
    }
  }, [isEdit, isReadMode, initialData, form]);

  useEffect(() => {
    if (Object.keys(formData).length > 0) {
      form.setFieldsValue(formData);
    }
  }, [currentStep, formData, form]);

  const validateCurrentStep = async () => {
    try {
      if (currentStep === 0) {
        await form.validateFields([
          "companyName",
          "companyPhone",
          "companyEmail",
        ]);
      }
      return true;
    } catch (error) {
      return false;
    }
  };

  const handleNext = async () => {
    if (!(await validateCurrentStep())) return;
    setFormData((prev) => ({ ...prev, ...form.getFieldsValue() }));
    setCurrentStep((prev) => prev + 1);
  };

  const handlePrevious = () => {
    setFormData((prev) => ({ ...prev, ...form.getFieldsValue() }));
    setCurrentStep((prev) => prev - 1);
  };

  const handleOk = async () => {
    try {
      await form.validateFields();
    } catch {
      return;
    }

    const allValues = { ...formData, ...form.getFieldsValue() };

    const payload = {};
    EDITABLE_FIELDS.forEach((f) => {
      payload[f] = DATE_FIELDS.includes(f)
        ? fmtDate(allValues[f])
        : allValues[f];
    });
    if (isEdit) payload.timeStamp = initialData?.timeStamp;

    form.resetFields();
    setFormData({});
    setCurrentStep(0);
    onSubmit(payload);
  };

  const handleCancel = () => {
    setFormData({});
    setCurrentStep(0);
    onCancel();
  };

  const renderNav = (isLast) =>
    !isReadMode && (
      <div className="d-flex flex-row-reverse gap-3">
        {isLast ? (
          <Button type="primary" onClick={handleOk}>
            {isEdit ? t("common.save") : t("common.add")}
          </Button>
        ) : (
          <Button type="primary" onClick={handleNext}>
            {t("common.next")}
          </Button>
        )}
        {currentStep > 0 && (
          <Button onClick={handlePrevious}>{t("common.previous")}</Button>
        )}
      </div>
    );

  return (
    <Modal
      title={
        isEdit ? t("companies.modal.editTitle") : t("companies.modal.addTitle")
      }
      open={open}
      onCancel={handleCancel}
      footer={null}
      destroyOnHidden
      width={800}
    >
      <div style={{ overflowX: "auto", marginBottom: 24 }}>
        <Steps
          current={currentStep}
          style={{ minWidth: 500 }}
          size="small"
          onChange={isReadMode ? (step) => setCurrentStep(step) : undefined}
          items={[
            { title: t("companies.modal.generalInformation") },
            { title: t("companies.modal.businessInformation") },
            { title: t("companies.modal.licenseInformation") },
          ]}
        />
      </div>

      {currentStep === 0 && (
        <Form
          disabled={isReadMode}
          form={form}
          layout="vertical"
          name="company_form"
        >
          <Row gutter={16}>
            {isEdit && (
              <Col span={12}>
                <Form.Item
                  name="companyCode"
                  label={t("companies.modal.companyCode")}
                >
                  <Input disabled placeholder={t("companies.modal.codeAuto")} />
                </Form.Item>
              </Col>
            )}

            {/* Company Name takes full width (24) when adding, half width (12) when editing/reading */}
            <Col span={isEdit || isReadMode ? 12 : 24}>
              <Form.Item
                name="companyName"
                label={t("companies.modal.companyName") || "Company Name"}
                rules={[
                  {
                    required: true,
                    message: t("common.required") || "Required",
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="companyPhone"
                label={t("companies.columns.phone")}
                rules={[
                  {
                    pattern: PHONE_PATTERN,
                    message: t("companies.modal.phoneInvalid"),
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="companyEmail"
                label={t("companies.columns.email")}
                rules={[
                  { type: "email", message: t("companies.modal.emailInvalid") },
                ]}
              >
                <Input type="email" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="companyTaxNumber"
                label={t("companies.modal.taxNumber")}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="companyFax" label="Fax">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="companyAddress"
                label={t("companies.modal.address")}
              >
                <Input.TextArea rows={2} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="companyTown" label={t("companies.columns.city")}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="companyPostalCode"
                label={t("companies.modal.postalCode")}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="companyCountry"
                label={t("companies.columns.country")}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="companyIsActive"
                label={t("companies.modal.active")}
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          {renderNav(false)}
        </Form>
      )}

      {currentStep === 1 && (
        <Form disabled={isReadMode} form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="companyTypeId"
                label={t("companies.modal.companyType")}
              >
                <Select placeholder={t("common.select")} allowClear>
                  {(companyTypes || [])
                    .filter((type) => type.companyTypeIsActive)
                    .map((type) => (
                      <Option
                        key={type.companyTypeId}
                        value={type.companyTypeId}
                      >
                        {type.companyTypeName}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="companyCurrency"
                label={t("companies.modal.currency")}
              >
                <Select
                  placeholder={t("common.select")}
                  allowClear
                  disabled={isReadMode}
                >
                  {AVAILABLE_CURRENCIES.map((curr) => (
                    <Option key={curr.code} value={curr.code}>
                      {curr.code} - {curr.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="companyBirthdate"
                label={t("companies.modal.birthdate")}
              >
                <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="companyEmployeesNumber"
                label={t("companies.modal.employeesNumber")}
              >
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="companyCapital"
                label={t("companies.modal.capital")}
              >
                <InputNumber min={0} precision={3} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>

          {renderNav(false)}
        </Form>
      )}

      {currentStep === 2 && (
        <Form disabled={isReadMode} form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="licenseStartDate"
                label={t("companies.modal.licenseStart")}
              >
                <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="licenseEndDate"
                label={t("companies.modal.licenseEnd")}
                rules={[
                  ({ getFieldValue }) => ({
                    validator: (_, v) =>
                      !v ||
                      !getFieldValue("licenseStartDate") ||
                      v.isAfter(getFieldValue("licenseStartDate"))
                        ? Promise.resolve()
                        : Promise.reject(
                            new Error(t("companies.modal.endAfterStart")),
                          ),
                  }),
                ]}
              >
                <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="licenseIsMonthlyPayed"
                label={t("companies.modal.licenseMonthlyPaid")}
                valuePropName="checked"
              >
                <Switch
                  onChange={(checked) => {
                    if (checked) {
                      // If monthly is checked, uncheck yearly
                      form.setFieldsValue({ licenseIsYearlyPayed: false });
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="licenseIsYearlyPayed"
                label={t("companies.modal.licenseYearlyPaid")}
                valuePropName="checked"
              >
                <Switch
                  onChange={(checked) => {
                    if (checked) {
                      // If yearly is checked, uncheck monthly
                      form.setFieldsValue({ licenseIsMonthlyPayed: false });
                    }
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="companyIsInTrialPeriod"
                label={t("companies.modal.inTrial")}
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="trialPeriodStartDate"
                label={t("companies.modal.trialStart")}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  format="DD/MM/YYYY"
                  disabled={!isInTrial}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="trialPeriodEndDate"
                label={t("companies.modal.trialEnd")}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  format="DD/MM/YYYY"
                  disabled={!isInTrial}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="usersNumber"
                label={t("companies.columns.users")}
              >
                <InputNumber style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="customersNumber"
                label={t("companies.modal.customersNumber")}
              >
                <InputNumber style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>

          {renderNav(true)}
        </Form>
      )}
    </Modal>
  );
};

CompanyFormModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isEdit: PropTypes.bool.isRequired,
  isReadMode: PropTypes.bool,
  initialData: PropTypes.object,
};

export default CompanyFormModal;
